// src/components/CartSidebar.tsx
import React, { useCallback, useEffect, useMemo } from 'react';
import { useCart } from '../context/CartContext';
import {
  FaTimes,
  FaTrash,
  FaMinus,
  FaPlus,
  FaShoppingCart,
  FaArrowRight,
  FaGift
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import T from './i18n/T';

const CartSidebar = () => {
  const {
    isCartOpen,
    setIsCartOpen,
    cartItems,
    removeFromCart,
    updateQuantity,
    cartTotal,
    cartTaxes,
    cartFinalTotal,
  } = useCart();

  const closeCart = useCallback(() => {
    setIsCartOpen(false);
  }, [setIsCartOpen]);

  // ✅ ESC close uniquement
  // ⚠️ IMPORTANT : on NE fait PAS de scroll-lock ici.
  // Le scroll-lock est géré dans CartContext (source de vérité) pour éviter les conflits.
  useEffect(() => {
    if (!isCartOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isCartOpen, closeCart]);

  // ✅ Normalise image/titre/prix/qty sans casser ton modèle existant
  const normalizedItems = useMemo(() => {
    return (cartItems || []).map((item: any) => ({
      ...item,
      _title: item.title || item.name || 'Article',
      _image:
        item.image_url ||
        item.image ||
        item.imageUrl ||
        item.imageURL ||
        'https://via.placeholder.com/150',
      _price: Number(item.price ?? 0),
      _qty: Number(item.quantity ?? 1),
      // On utilise le cart_item_id s'il existe, sinon on fallback pour rétrocompatibilité.
      _key: item.cart_item_id || `${item.type || 'product'}_${item.id}`,
      _lineId: item.cart_item_id || item.id,
    }));
  }, [cartItems]);

  if (!isCartOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Panier"
    >
      {/* Overlay sombre */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeCart}
      />

      {/* Panneau Latéral */}
      <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-900 text-white">
          <h2 className="font-black text-lg flex items-center gap-2">
            <FaShoppingCart /> <T fr="Mon Panier" en="My Cart" />
          </h2>
          <button
            onClick={closeCart}
            className="p-2 hover:bg-white/20 rounded-full transition"
            aria-label="Fermer le panier"
          >
            <FaTimes />
          </button>
        </div>

        {/* ✅ Scroll interne fiable + safe-area iPhone */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 overscroll-contain pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
          {normalizedItems.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <FaShoppingCart size={48} className="mx-auto mb-4 opacity-20" />
              <p><T fr="Votre panier est vide." en="Your cart is empty." /></p>
              <button
                onClick={closeCart}
                className="mt-4 text-blue-600 font-bold underline"
              >
                <T fr="Continuer mes achats" en="Continue shopping" />
              </button>
            </div>
          ) : (
            normalizedItems.map((item: any) => (
              <div
                key={item._key}
                className={`flex gap-4 p-3 rounded-xl border ${item.is_gift ? 'bg-pink-50/50 border-pink-100' : 'bg-slate-50 border-slate-100'}`}
              >
                <div className="w-20 h-20 bg-white rounded-lg overflow-hidden flex-shrink-0 relative">
                  <img
                    src={item._image}
                    alt={item._title}
                    className="w-full h-full object-cover"
                  />
                  {item.is_gift && (
                    <div className="absolute top-1 right-1 bg-pink-500 text-white p-1 rounded-full shadow-sm">
                      <FaGift size={10} />
                    </div>
                  )}
                </div>

                <div className="flex-1 flex flex-col justify-between min-w-0">
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm line-clamp-1">
                      {item._title}
                    </h4>
                    <p className="text-xs text-slate-500 mb-1">{item._price.toFixed(2)}$ / unité</p>
                    
                    {/* ✅ BADGE CADEAU */}
                    {item.is_gift && item.gift_info && (
                      <div className="inline-flex items-center gap-1.5 bg-pink-100 text-pink-700 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider mb-2">
                        <FaGift size={10} /> Pour : {item.gift_info.recipient_name}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-end">
                    
                    {/* Pour un cadeau, la quantité est fixe à 1. Sinon on affiche les boutons classiques */}
                    {item.is_gift ? (
                      <div className="text-xs font-bold text-slate-500 px-2 py-1 bg-white rounded-lg border border-slate-200">
                        <T fr="Qté: 1" en="Qty: 1" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 bg-white rounded-lg px-2 py-1 border border-slate-200 shadow-sm">
                        <button
                          onClick={() => updateQuantity(item._lineId, -1)}
                          className="text-xs p-1 hover:text-blue-600"
                          aria-label="Diminuer la quantité"
                        >
                          <FaMinus />
                        </button>
                        <span className="text-xs font-bold w-4 text-center">
                          {item._qty}
                        </span>
                        <button
                          onClick={() => updateQuantity(item._lineId, 1)}
                          className="text-xs p-1 hover:text-blue-600"
                          aria-label="Augmenter la quantité"
                        >
                          <FaPlus />
                        </button>
                      </div>
                    )}

                    <button
                      onClick={() => removeFromCart(item._lineId)}
                      className="text-red-400 hover:text-red-600 p-1"
                      aria-label="Retirer l'article"
                    >
                      <FaTrash size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {normalizedItems.length > 0 && (
          <div className="p-6 border-t border-slate-100 bg-slate-50">
            <div className="space-y-2 mb-4 text-sm">
              <div className="flex justify-between items-center text-slate-500 font-bold">
                <span><T fr="Sous-total" en="Subtotal" /></span>
                <span>{(Number(cartTotal) || 0).toFixed(2)}$</span>
              </div>
              <div className="flex justify-between items-center text-slate-500 font-bold">
                <span><T fr="Taxes estimées" en="Estimated taxes" /></span>
                <span>{(Number(cartTaxes) || 0).toFixed(2)}$</span>
              </div>
              <div className="flex justify-between items-end pt-3 border-t border-slate-200">
                <span className="text-slate-700 font-black"><T fr="Total estimé" en="Estimated total" /></span>
                <span className="text-2xl font-black text-slate-900">
                  {(Number(cartFinalTotal) || 0).toFixed(2)}$
                </span>
              </div>
              <p className="text-[10px] text-slate-400 leading-tight">
                <T fr="La livraison, si applicable, sera calculée au paiement. Les montants finaux sont validés par Stripe." en="Delivery, if applicable, will be calculated at checkout. Final amounts are validated by Stripe." />
              </p>
            </div>

            <Link
              to="/checkout"
              onClick={closeCart}
              className="w-full bg-slate-900 text-white py-4 rounded-xl font-black uppercase tracking-widest hover:bg-green-600 transition flex items-center justify-center gap-2 shadow-lg"
            >
              <T fr="Payer maintenant" en="Pay now" /> <FaArrowRight />
            </Link>

            {/* Safe-area bottom iPhone */}
            <div className="h-[env(safe-area-inset-bottom)]" />
          </div>
        )}
      </div>
    </div>
  );
};

export default CartSidebar;
